import { useState, useEffect } from 'react';
import { FiEye } from 'react-icons/fi';
import Toggle from '~/components/Toggle';
import { RxCross2 } from 'react-icons/rx';
import { BsArrowUpRight } from 'react-icons/bs';
import { IMessage } from '~/types/campaigns';
import moment from 'moment';
import { useAuth } from '~/context/AuthProvider';
import { Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import AsyncSelect from 'react-select/async';
import { fetchAssignees } from '~/services/campaign';
import TipTapEditor from '~/components/tiptap/TipTapEditor';

type Props = {
    message: IMessage;
    onDelete: (id: number, campaignRequestId?: number | null) => void;
    updateMessage: (
        id: number,
        payload: any,
        campaignRequestId?: number | null
    ) => void;
};

const modules = {
    toolbar: [
        ['bold', 'italic', 'underline'],
        [{ align: 'right' }, { align: 'center' }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'image'],
    ],
};

interface FormValues {
    message: string;
    assignTo: any;
    campaign: string;
    note: string;
    tags: string;
    todoCompleted: any;
}

const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'list',
    'bullet',
    'indent',
    'link',
    'image',
];

const ViewMessageModal = ({ message, onDelete, updateMessage }: Props) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [isVisible, setIsVisible] = useState(message.isPinned);
    const closeModal = () => setModalOpen(false);
    const { person } = useAuth();
    const [assigneeOptions, setAssigneeOptions] = useState<any>([]);
    const { control, handleSubmit, reset, watch, setValue } =
        useForm<FormValues>({});

    useEffect(() => {
        const keyHandler = ({ keyCode }: KeyboardEvent) => {
            if (keyCode === 27) closeModal();
        };
        document.addEventListener('keydown', keyHandler);
        return () => document.removeEventListener('keydown', keyHandler);
    }, []);

    useEffect(() => {
        reset({
            note: message.message,
            todoCompleted: !!message.completed_date,
            assignTo: message.assignee,
        });
    }, [message, reset]);

    useEffect(() => {
        if (assigneeOptions.length) {
            const assignee = findAssigneeByName(
                message.assignee,
                assigneeOptions
            );
            if (assignee) {
                setValue('assignTo', {
                    value: assignee.value,
                    label: assignee.label,
                });
            }
        }
    }, [assigneeOptions]);

    const handleDelete = () => {
        if (
            window.confirm(
                'Are you sure you want to delete this todo? This action cannot be undone.'
            )
        ) {
            onDelete(message.id, message.campaign_request_id);
            closeModal();
        }
    };

    const handleSave = async (data: FormValues) => {
        // todo_flag is deliberately omitted: this form has no to-do control,
        // and both note endpoints leave the flag alone when it isn't sent.
        // Sending true here converted every edited note into a to-do.
        const payload = {
            note: data.note,
            pinned_flag: isVisible,
            completed: Boolean(data.todoCompleted),
            ...(data.assignTo
                ? { assignee: parseInt(data.assignTo.value) }
                : {}),
        };
        updateMessage(message.id, payload, message.campaign_request_id);
        closeModal();
    };

    useEffect(() => {
        setIsVisible(message.isPinned);
    }, [message.isPinned]);

    function findAssigneeByName(name: string, assignees: any) {
        const found = assignees.find((assignee: any) => {
            // Try matching by name first
            const nameMatch = assignee.label.trim() === name;
            // Try matching by ID (value field)
            const idMatch = assignee.value.toString() === name.toString();
            return nameMatch || idMatch;
        });
        return found;
    }
    const loadAssigneeOptions = async () => {
        const assignees = await fetchAssignees();

        const mappedAssignees = assignees?.flatMap((group: any[]) => {
            const groupMembers = group[1];

            return groupMembers.map((member: any) => ({
                value: member[0],
                label: member[1],
            }));
        });
        setAssigneeOptions(mappedAssignees);

        return mappedAssignees;
    };
    return (
        <>
            <button onClick={() => setModalOpen(!modalOpen)}>
                <FiEye className='text-primary' />
            </button>

            {modalOpen && (
                <div
                    className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75'
                    onClick={closeModal}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className='relative w-full max-w-xl rounded-lg bg-white shadow-lg'
                    >
                        <button
                            className='text-gray-600 hover:text-gray-900 absolute right-4 top-4'
                            onClick={closeModal}
                        >
                            <RxCross2 />
                        </button>
                        <div className='p-8'>
                            <div className='mb-[30px]'>
                                <h2 className='mb-[30px] text-[22px] font-bold'>
                                    View Message
                                </h2>
                                <p className='text-gray-600 bg-[#f4f4f5] px-3 py-1 text-base'>
                                    <span className='mr-3 text-xs text-[#acadb0]'>
                                        {moment(message.date).isValid()
                                            ? moment(message.date).format(
                                                  'MM/DD/YYYY'
                                              )
                                            : ''}
                                    </span>
                                    <span>
                                        Created by {message.created_by_name}
                                    </span>
                                </p>
                                {watch('todoCompleted') && (
                                    <p className='text-gray-600 bg-[#f4f4f5] px-3 py-1 text-base'>
                                        <span className='mr-3 text-xs text-[#acadb0]'>
                                            {message.completed_date
                                                ? moment(
                                                      message.completed_date
                                                  ).format('MM/DD/YYYY')
                                                : moment().format('MM/DD/YYYY')}
                                        </span>
                                        <span>
                                            To Do Completed by{' '}
                                            {message?.completed_by_name ||
                                                person?.first_name ||
                                                person?.last_name}
                                        </span>
                                    </p>
                                )}
                            </div>
                            <form onSubmit={handleSubmit(handleSave)}>
                                <div className='mb-4 grid grid-cols-2 gap-4'>
                                    <div>
                                        <label className='block text-xs font-semibold'>
                                            Assign To
                                        </label>
                                        <div className='relative'>
                                            <div className='absolute inset-y-0 right-2.5 flex items-center'>
                                                <svg
                                                    className='text-gray-500 dark:text-gray-400 h-4 w-4'
                                                    aria-hidden='true'
                                                    xmlns='http://www.w3.org/2000/svg'
                                                    fill='none'
                                                    viewBox='0 0 20 20'
                                                >
                                                    <path
                                                        stroke='currentColor'
                                                        strokeLinecap='round'
                                                        strokeLinejoin='round'
                                                        strokeWidth='2'
                                                        d='m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z'
                                                    />
                                                </svg>
                                            </div>
                                            <Controller
                                                name='assignTo'
                                                control={control}
                                                render={({ field }) => (
                                                    <AsyncSelect
                                                        placeholder='Select Assignee'
                                                        cacheOptions
                                                        loadOptions={
                                                            loadAssigneeOptions
                                                        }
                                                        defaultOptions
                                                        isSearchable={false}
                                                        styles={{
                                                            control: (
                                                                baseStyles
                                                            ) => ({
                                                                ...baseStyles,
                                                                borderColor:
                                                                    'black',
                                                                borderRadius:
                                                                    '0.375rem',
                                                            }),
                                                            menu: (
                                                                baseStyles
                                                            ) => ({
                                                                ...baseStyles,
                                                                zIndex: 9999,
                                                            }),
                                                        }}
                                                        components={{
                                                            IndicatorSeparator:
                                                                null,
                                                        }}
                                                        {...field}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className='block text-xs font-semibold'>
                                            Campaign
                                        </label>
                                        <Link
                                            to={`/campaigns/${message.campaign_id}`}
                                            target='_blank'
                                            className='text-blue-500 mt-4 text-sm text-primary'
                                        >
                                            {message.campaign}{' '}
                                            <BsArrowUpRight />
                                        </Link>
                                    </div>
                                </div>
                                <div className='mb-4 flex items-center'>
                                    <Controller
                                        name='todoCompleted'
                                        control={control}
                                        render={({ field }) => (
                                            <input
                                                type='checkbox'
                                                className='mr-2 text-sm'
                                                id='completed'
                                                checked={field.value}
                                                {...field}
                                            />
                                        )}
                                    />
                                    <label
                                        className='text-xs'
                                        htmlFor='completed'
                                    >
                                        To Do completed
                                    </label>
                                </div>
                                <div className='mb-4'>
                                    <label className='block text-xs font-semibold'>
                                        Message
                                    </label>

                                    <Controller
                                        control={control}
                                        name='note'
                                        rules={{
                                            required: 'Message is required',
                                        }}
                                        render={({
                                            field: { onChange, value, onBlur },
                                        }) => {
                                            return (
                                                <TipTapEditor
                                                    initialValue={value}
                                                    onChange={onChange}
                                                />
                                            );
                                        }}
                                    />
                                </div>
                                <div className='mb-4 flex items-center'>
                                    <Toggle
                                        isOn={isVisible}
                                        setIsOn={setIsVisible}
                                    />
                                    <span className='ml-2 text-xs font-semibold'>
                                        Pinned
                                    </span>
                                </div>
                                {/* <div className='mb-[30px]'>
                                    <label className='block text-xs font-semibold'>
                                        Tags
                                    </label>
                                    <input
                                        type='text'
                                        className='border-gray-300 mt-1 w-full rounded-md border px-3 py-2 text-xs text-xs outline-none focus:border-primary'
                                        placeholder='Add tag'
                                    />
                                </div> */}
                                <div className='flex justify-start space-x-4'>
                                    <button
                                        type='button'
                                        onClick={closeModal}
                                        className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-semibold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type='button'
                                        onClick={handleDelete}
                                        className='rounded-full bg-danger px-[24px] py-[7px] text-[14px]/[18px] font-semibold uppercase text-black transition-colors duration-500 hover:bg-red'
                                    >
                                        Delete
                                    </button>
                                    <button
                                        type='submit'
                                        className='rounded-full bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-semibold uppercase text-black transition-colors duration-500 hover:bg-yellow '
                                    >
                                        Save
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ViewMessageModal;
